import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./Header.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import WbCloudyIcon from "@mui/icons-material/WbCloudy";
import CloudIcon from "@mui/icons-material/Cloud";
import UmbrellaIcon from "@mui/icons-material/Umbrella";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FoggyIcon from "@mui/icons-material/Foggy";
import { AIRPORTS, REGIONAL_AIRSPACE } from "../constants";
import {
  getCurrentGMTTime,
  fahrenheitToCelsius,
  getWeatherIconName,
  getWeatherIconColor,
} from "../utils";

function Header({ selectedHub, weatherData, weatherLoading }) {
  const { t, i18n } = useTranslation();
  const [currentTime, setCurrentTime] = useState("");

  // Icon mapping
  const iconComponents = {
    WbSunny: WbSunnyIcon,
    WbCloudy: WbCloudyIcon,
    Cloud: CloudIcon,
    Umbrella: UmbrellaIcon,
    Thunderstorm: ThunderstormIcon,
    AcUnit: AcUnitIcon,
    Foggy: FoggyIcon,
  };

  // Helper function to get weather icon component
  // At night, clear/sunny conditions show a moon icon instead of sun
  const getWeatherIcon = (condition, isDayTime) => {
    if (!condition) return null;

    const conditionLower = condition.toLowerCase();
    const isClear =
      conditionLower.includes("sunny") || conditionLower.includes("clear");

    if (isClear && isDayTime === 0) {
      return (
        <DarkModeIcon
          fontSize="small"
          style={{ color: "#5b7c99", verticalAlign: "middle", marginRight: 4 }}
        />
      );
    }

    const iconName = getWeatherIconName(condition);
    const IconComponent = iconComponents[iconName] || CloudIcon;
    const color = getWeatherIconColor(condition);

    return (
      <IconComponent
        fontSize="small"
        style={{ color, verticalAlign: "middle", marginRight: 4 }}
      />
    );
  };

  // Get display name based on hub - memoized
  const displayName = useMemo(() => {
    if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
      return t("header.regionalAirspace");
    }
    const airport = AIRPORTS.find((a) => a.hubCode === selectedHub);
    if (!airport) return "";

    // Map IATA codes to translation keys
    const translationKeyMap = {
      ACC: "kotoka",
      LOS: "murtala",
      ABJ: "felix",
      DSS: "leopold",
      CKY: "conakry",
      BKO: "bamako",
    };

    const translationKey = translationKeyMap[airport.iataCode];
    return translationKey ? t(`airports.${translationKey}`) : airport.name;
  }, [selectedHub, t]);

  // Generate breadcrumb path - memoized
  const breadcrumbPath = useMemo(() => {
    if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
      return `${t("header.mainDashboard")} > ${t("header.regionalAirspace")}`;
    }
    return `${t("header.westAfricanAirports")} > ${selectedHub}`;
  }, [selectedHub, t]);

  // Update time every second — local time for single hub, GMT for regional
  useEffect(() => {
    const getLocalTime = () => {
      const now = new Date();
      if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
        const hours = now.getUTCHours().toString().padStart(2, "0");
        const minutes = now.getUTCMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes} GMT`;
      }
      const airport = AIRPORTS.find((a) => a.hubCode === selectedHub);
      if (!airport?.timezone) return getCurrentGMTTime();
      return now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: airport.timezone,
        timeZoneName: "short",
      });
    };

    setCurrentTime(getLocalTime());
    const timeInterval = setInterval(
      () => setCurrentTime(getLocalTime()),
      1000,
    );
    return () => clearInterval(timeInterval);
  }, [selectedHub]);

  // Derive weather display values from shared weatherData prop
  const { temperature, tempRange, weatherCondition, weatherConditions, isDay } =
    useMemo(() => {
      if (
        weatherLoading ||
        !weatherData ||
        Object.keys(weatherData).length === 0
      ) {
        return {
          temperature: null,
          tempRange: null,
          weatherCondition: null,
          weatherConditions: null,
          isDay: null,
        };
      }

      if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
        const temperatures = AIRPORTS.map((airport) => {
          const data = weatherData[airport.iataCode];
          if (!data?.current_weather) return null;
          const temp = data.current_weather.temperature;
          return i18n.language === "fr"
            ? fahrenheitToCelsius(temp)
            : Math.round(temp);
        }).filter((t) => t !== null);

        const conditions = AIRPORTS.map((airport) => {
          const data = weatherData[airport.iataCode];
          return (
            data?.current_weather?.condition ||
            data?.current_weather?.description
          );
        }).filter(Boolean);

        if (temperatures.length === 0) {
          return {
            temperature: null,
            tempRange: null,
            weatherCondition: null,
            weatherConditions: null,
            isDay: null,
          };
        }

        const minTemp = Math.round(Math.min(...temperatures));
        const maxTemp = Math.round(Math.max(...temperatures));

        const uniqueConditions = [...new Set(conditions)];
        const weatherSeverity = {
          Sunny: 1,
          Clear: 1,
          "Partly cloudy": 2,
          "Partly Cloudy": 2,
          Cloudy: 3,
          Overcast: 3,
          Rainy: 4,
          Rain: 4,
          Drizzle: 4,
          Shower: 4,
          Thunderstorm: 5,
          Storm: 5,
          Snow: 6,
          Fog: 7,
          Mist: 7,
        };
        const sortedConditions = uniqueConditions.sort(
          (a, b) => (weatherSeverity[a] || 3) - (weatherSeverity[b] || 3),
        );

        // All hubs are in the same timezone band — use any hub's is_day
        const anyIsDay = AIRPORTS.reduce((val, airport) => {
          if (val != null) return val;
          return weatherData[airport.iataCode]?.current_weather?.is_day ?? null;
        }, null);

        return {
          temperature: null,
          tempRange: { min: minTemp, max: maxTemp },
          weatherCondition: null,
          weatherConditions: {
            min: sortedConditions[0],
            max: sortedConditions[sortedConditions.length - 1],
          },
          isDay: anyIsDay,
        };
      }

      // Single hub
      const airport = AIRPORTS.find((a) => a.hubCode === selectedHub);
      if (!airport || !weatherData[airport.iataCode]?.current_weather) {
        return {
          temperature: null,
          tempRange: null,
          weatherCondition: null,
          weatherConditions: null,
          isDay: null,
        };
      }

      const data = weatherData[airport.iataCode].current_weather;
      const temp = data.temperature;
      const roundedTemp =
        i18n.language === "fr" ? fahrenheitToCelsius(temp) : Math.round(temp);
      const condition = data.condition || data.description;

      return {
        temperature: roundedTemp,
        tempRange: null,
        weatherCondition: condition,
        weatherConditions: null,
        isDay: data.is_day,
      };
    }, [weatherData, weatherLoading, selectedHub, i18n.language]);

  const loading = weatherLoading;

  return (
    <div className="header">
      <div className="breadcrumb-path">{breadcrumbPath}</div>
      <div className="header-box">
        <div className="header-left">
          <div className="breadcrumb-path-mobile">{breadcrumbPath}</div>
          <h1 className="airport-title">{displayName}</h1>
        </div>
        <div className="header-info">
          <div className="airport-details">
            <div className="detail-item">
              <span className="label">
                {selectedHub === REGIONAL_AIRSPACE.hubCode
                  ? t("header.temperatureRange")
                  : t("header.temperature")}
              </span>
              <span className="value">
                {selectedHub === REGIONAL_AIRSPACE.hubCode &&
                weatherConditions ? (
                  <>
                    {getWeatherIcon(weatherConditions.min, isDay)}
                    {loading
                      ? "..."
                      : tempRange
                        ? `${tempRange.min}°${i18n.language === "fr" ? "C" : "F"} - `
                        : "N/A"}
                    {getWeatherIcon(weatherConditions.max, isDay)}
                    {!loading &&
                      tempRange &&
                      `${tempRange.max}°${i18n.language === "fr" ? "C" : "F"}`}
                  </>
                ) : (
                  <>
                    {getWeatherIcon(weatherCondition, isDay)}
                    {loading
                      ? "..."
                      : temperature !== null
                        ? `${temperature}°${i18n.language === "fr" ? "C" : "F"}`
                        : "N/A"}
                  </>
                )}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">{t("header.localTime")}</span>
              <span className="value">
                <AccessTimeIcon
                  fontSize="small"
                  style={{ verticalAlign: "middle", marginRight: 4 }}
                />
                {currentTime}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
